import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  numberAttribute,
} from '@angular/core';
import { DsButtonV2Component } from '../buttons/ds-button-v2.component';
import { DsIconComponent } from '../icons/ds-icon.component';
import type { DsIconName } from '../icons/ds-icons';

export type DsInputFileState = 'default' | 'uploading' | 'success' | 'error';

@Component({
  selector: 'ds-input-file',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DsButtonV2Component, DsIconComponent],
  template: `
    <input
      #fileInput
      class="ds-input-file__native"
      type="file"
      [attr.accept]="accept || null"
      [attr.multiple]="multiple ? '' : null"
      [disabled]="disabled"
      (change)="onFilesSelected($event)"
    />

    <div
      class="ds-input-file"
      [class.ds-input-file--uploading]="state === 'uploading'"
      [class.ds-input-file--success]="state === 'success'"
      [class.ds-input-file--error]="state === 'error'"
      [class.ds-input-file--disabled]="disabled"
      role="group"
      [attr.aria-label]="ariaLabel"
      [attr.aria-busy]="state === 'uploading' ? 'true' : null"
      [attr.aria-invalid]="state === 'error' ? 'true' : null"
      [attr.aria-disabled]="disabled ? 'true' : null"
    >
      <div class="ds-input-file__content">
        @if (showIcon) {
          <ds-icon
            class="ds-input-file__icon"
            [class.ds-input-file__icon--spinning]="state === 'uploading'"
            [name]="iconName"
            size="var(--ds-input-file-icon-size)"
          />
        }

        <div class="ds-input-file__copy">
          <span class="ds-input-file__headline">{{ headline }}</span>
          @if (state === 'success') {
            <span class="ds-input-file__description">
              <span class="ds-input-file__success-label">{{ successLabel }}@if (fileName) {<span aria-hidden="true"> · </span>}</span>
              @if (fileName) {
                <span class="ds-input-file__file-name" [title]="fileName">{{ fileName }}</span>
              }
            </span>
          } @else {
            <span class="ds-input-file__description">{{ description }}</span>
          }
        </div>
      </div>

      @if (state === 'uploading') {
        <div
          class="ds-input-file__progress"
          role="progressbar"
          [attr.aria-label]="progressLabel"
          aria-valuemin="0"
          aria-valuemax="100"
          [attr.aria-valuenow]="progressPercent"
          [style.--ds-input-file-progress]="progressPercent + '%'"
        >
          <span class="ds-input-file__track">
            <span class="ds-input-file__indicator"></span>
          </span>
          <span class="ds-input-file__progress-label">%{{ progressPercent }}</span>
        </div>
      } @else if (state === 'default') {
        <div class="ds-input-file__actions">
          <ds-button-v2
            [label]="uploadLabel"
            emphasis="secondary-outlined"
            size="xs"
            [disabled]="disabled"
            (clicked)="openFileDialog()"
          />
        </div>
      } @else if (state === 'success' && (showReviewButton || showDeleteButton)) {
        <div class="ds-input-file__actions">
          @if (showReviewButton) {
            <ds-button-v2
              [label]="reviewLabel"
              emphasis="secondary-outlined"
              size="xs"
              [disabled]="disabled"
              (clicked)="reviewFile()"
            />
          }
          @if (showDeleteButton) {
            <ds-button-v2
              [label]="deleteLabel"
              emphasis="secondary-outlined"
              size="xs"
              [disabled]="disabled"
              (clicked)="deleteFile()"
            />
          }
        </div>
      } @else if (state === 'error') {
        <div class="ds-input-file__actions">
          <ds-button-v2
            [label]="retryLabel"
            emphasis="secondary-outlined"
            size="xs"
            [disabled]="disabled"
            (clicked)="retryUpload()"
          />
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      max-width: var(--ds-input-file-max-width);
    }

    .ds-input-file__native {
      display: none;
    }

    .ds-input-file {
      display: flex;
      flex-wrap: wrap;
      align-content: center;
      align-items: center;
      justify-content: space-between;
      box-sizing: border-box;
      width: 100%;
      min-width: min(100%, var(--ds-input-file-min-width));
      min-height: var(--ds-input-file-min-height);
      padding: var(--ds-input-file-padding);
      row-gap: var(--ds-input-file-section-gap);
      border: var(--ds-input-file-border-width) dashed var(--ds-input-file-border-color);
      border-radius: var(--ds-input-file-border-radius);
      background: var(--ds-input-file-background);
      color: var(--ds-input-file-title-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
    }

    .ds-input-file--uploading {
      column-gap: var(--ds-input-file-uploading-column-gap);
      background: var(--ds-input-file-uploading-background);
    }

    .ds-input-file--error {
      border-color: var(--ds-input-file-error-border-color);
    }

    .ds-input-file--disabled {
      opacity: calc(var(--ds-input-file-disabled-opacity) / 100px);
    }

    .ds-input-file__content {
      display: flex;
      flex: 0 1 var(--ds-input-file-content-min-width);
      align-items: center;
      box-sizing: border-box;
      width: min(100%, var(--ds-input-file-content-min-width));
      min-width: min(100%, var(--ds-input-file-content-min-width));
      max-width: var(--ds-input-file-content-max-width);
      gap: var(--ds-input-file-content-gap);
    }

    .ds-input-file__icon {
      flex: 0 0 var(--ds-input-file-icon-size);
      color: var(--ds-input-file-icon-color);
    }

    .ds-input-file__icon--spinning {
      animation: ds-input-file-spin calc(var(--ds-input-file-spinner-duration) / 1px * 1ms) linear infinite;
    }

    .ds-input-file--success .ds-input-file__icon,
    .ds-input-file__success-label {
      color: var(--ds-input-file-success-color);
    }

    .ds-input-file--error .ds-input-file__icon,
    .ds-input-file--error .ds-input-file__description {
      color: var(--ds-input-file-error-color);
    }

    .ds-input-file__copy {
      display: flex;
      flex: 1 1 var(--ds-input-file-copy-max-width);
      flex-direction: column;
      min-width: 0;
      max-width: var(--ds-input-file-copy-max-width);
      gap: var(--ds-input-file-copy-gap);
    }

    .ds-input-file__headline,
    .ds-input-file__description {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ds-input-file__headline {
      color: var(--ds-input-file-title-color);
      font-size: var(--ds-input-file-title-font-size);
      font-weight: var(--ds-input-file-title-font-weight);
      line-height: var(--ds-input-file-title-line-height);
    }

    .ds-input-file__description {
      display: flex;
      min-width: 0;
      color: var(--ds-input-file-description-color);
      font-size: var(--ds-input-file-description-font-size);
      font-weight: var(--ds-input-file-description-font-weight);
      line-height: var(--ds-input-file-description-line-height);
    }

    .ds-input-file__success-label {
      flex: 0 0 auto;
    }

    .ds-input-file__file-name {
      min-width: 0;
      overflow: hidden;
      color: var(--ds-input-file-description-color);
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ds-input-file__actions {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: flex-end;
      gap: var(--ds-input-file-actions-gap);
    }

    .ds-input-file__progress {
      display: flex;
      flex: 1 1 var(--ds-input-file-progress-min-width);
      align-items: center;
      min-width: min(100%, var(--ds-input-file-progress-min-width));
      gap: var(--ds-input-file-progress-gap);
    }

    .ds-input-file__track {
      position: relative;
      display: block;
      flex: 1 1 auto;
      height: var(--ds-input-file-progress-track-height);
      overflow: hidden;
      border-radius: var(--ds-input-file-progress-track-radius);
      background: var(--ds-input-file-progress-track-color);
    }

    .ds-input-file__indicator {
      position: absolute;
      inset: 0 auto 0 0;
      width: var(--ds-input-file-progress, 0%);
      border-radius: inherit;
      background: var(--ds-input-file-progress-indicator-color);
    }

    .ds-input-file__progress-label {
      flex: 0 0 auto;
      color: var(--ds-input-file-description-color);
      font-size: var(--ds-input-file-progress-label-font-size);
      font-weight: var(--ds-input-file-progress-label-font-weight);
      line-height: var(--ds-input-file-progress-label-line-height);
      white-space: nowrap;
    }

    @keyframes ds-input-file-spin {
      to { transform: rotate(360deg); }
    }

    @media (prefers-reduced-motion: reduce) {
      .ds-input-file__icon--spinning {
        animation: none;
      }
    }
  `],
})
export class DsInputFileComponent {
  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;

  @Input() headline = 'Documento solicitado';
  @Input() description = 'Texto bajada';
  @Input() state: DsInputFileState = 'default';
  @Input({ transform: numberAttribute }) progress = 50;
  @Input() accept = '';
  @Input({ transform: booleanAttribute }) multiple = false;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) showIcon = true;
  @Input() fileName = '';
  @Input({ transform: booleanAttribute }) showReviewButton = true;
  @Input({ transform: booleanAttribute }) showDeleteButton = true;
  @Input() uploadLabel = 'Cargar documento';
  @Input() successLabel = 'Completado';
  @Input() reviewLabel = 'Revisar';
  @Input() deleteLabel = 'Eliminar';
  @Input() retryLabel = 'Reintentar';
  @Input() ariaLabel = 'Carga de archivo';
  @Input() progressLabel = 'Progreso de carga';

  @Output() filesSelected = new EventEmitter<readonly File[]>();
  @Output() stateChange = new EventEmitter<DsInputFileState>();
  @Output() fileNameChange = new EventEmitter<string>();
  @Output() reviewRequested = new EventEmitter<void>();
  @Output() deleteRequested = new EventEmitter<void>();
  @Output() retryRequested = new EventEmitter<void>();

  get progressPercent(): number {
    return Math.round(Math.min(100, Math.max(0, this.progress)));
  }

  get iconName(): DsIconName {
    switch (this.state) {
      case 'uploading': return 'ProgressActivity';
      case 'success': return 'CheckCircle';
      case 'error': return 'Error';
      default: return 'Description';
    }
  }

  openFileDialog(): void {
    if (this.disabled || this.state === 'uploading') return;
    this.fileInput?.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (files.length > 0) {
      this.fileName = files.map(({ name }) => name).join(', ');
      this.state = 'success';
      this.fileNameChange.emit(this.fileName);
      this.stateChange.emit(this.state);
      this.filesSelected.emit(files);
    }
    input.value = '';
  }

  reviewFile(): void {
    if (this.disabled) return;
    this.reviewRequested.emit();
  }

  deleteFile(): void {
    if (this.disabled) return;
    if (this.fileInput) this.fileInput.nativeElement.value = '';
    this.fileName = '';
    this.state = 'default';
    this.fileNameChange.emit(this.fileName);
    this.stateChange.emit(this.state);
    this.deleteRequested.emit();
  }

  retryUpload(): void {
    if (this.disabled) return;
    this.retryRequested.emit();
  }
}
